import { StyleSheet, css } from 'aphrodite';
import { type CSSProperties, type FC, useState } from 'react';
import { toast } from 'sonner';
import { FormFieldType } from '../../../extViewmodels';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { type FocusedLeadAttribute, useLeadAttributeOptions, useUpdateLeadAttribute } from '../data';
import { readableInk } from './colorContrast';

// Not built on components-v2 PillSelect: that can't lazy-load options on open, carries no per-option hexColor, and has no per-field write path.
type LeadAttributePillProps = {
	companyId: string;
	attribute: FocusedLeadAttribute;
};

function pillColors(hexColor: string | undefined, isUnknown: boolean): CSSProperties {
	if (hexColor) return { background: hexColor, color: readableInk(hexColor) };
	if (isUnknown) return { background: 'var(--attr-unknown-bg)', color: 'var(--attr-unknown-ink)' };
	return { background: 'var(--attr-bg)', color: 'var(--attr-ink)' };
}

export const LeadAttributePill: FC<LeadAttributePillProps> = ({ companyId, attribute }) => {
	const [open, setOpen] = useState(false);
	const [pendingLabel, setPendingLabel] = useState<string | null>(null);
	const editable = attribute.fieldType === FormFieldType.Option;
	// Options load on demand — only once the dropdown opens.
	const { options, isLoading } = useLeadAttributeOptions(companyId, editable ? attribute.id : undefined, open);
	const updateAttribute = useUpdateLeadAttribute();

	const saving = updateAttribute.isPending && pendingLabel !== null;
	const displayValue = saving ? pendingLabel : attribute.value;
	const colors = pillColors(attribute.hexColor, !displayValue);
	const content = (
		<>
			<span className={css(styles.label)}>{attribute.label}:</span>
			<span>{displayValue || 'Unknown'}</span>
		</>
	);

	if (!editable) {
		return (
			<span className={css(styles.pill)} style={colors} title={attribute.label}>
				{content}
			</span>
		);
	}

	const handleSelect = (optionId: string, optionLabel: string) => {
		setOpen(false);
		setPendingLabel(optionLabel);
		updateAttribute.mutate(
			{ companyId, fieldId: attribute.id, value: optionId },
			{
				onError: () => toast.error(`Couldn't update ${attribute.label}`),
				// Drop the optimistic label once the write settles — on success the
				// refetched lead carries the new value; on error we revert.
				onSettled: () => setPendingLabel(null),
			}
		);
	};

	return (
		<Dropdown open={open} onOpenChange={setOpen}>
			<DropdownTrigger
				className={css(styles.pill, styles.pillButton)}
				style={colors}
				disabled={updateAttribute.isPending}
				title={`Edit ${attribute.label}`}
			>
				{content}
				<span className={css(styles.caret)} aria-hidden='true'>
					▾
				</span>
			</DropdownTrigger>
			<DropdownContent>
				{isLoading && <DropdownItem disabled>Loading…</DropdownItem>}
				{!isLoading && options.length === 0 && <DropdownItem disabled>No options</DropdownItem>}
				{options.map(option => (
					<DropdownItem key={option.id} onClick={() => handleSelect(option.id, option.label)}>
						{option.label}
					</DropdownItem>
				))}
			</DropdownContent>
		</Dropdown>
	);
};

const styles = StyleSheet.create({
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		height: 24,
		paddingLeft: 10,
		paddingRight: 10,
		// Intentional fully-rounded pill shape (not the 8px theme default).
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		border: 'none',
		whiteSpace: 'nowrap',
	},
	pillButton: {
		cursor: 'pointer',
		fontFamily: 'inherit',
	},
	label: {
		opacity: 0.7,
	},
	caret: {
		fontSize: 9,
		marginLeft: 2,
	},
});
